import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, ArrowRight, FileText, ExternalLink, FolderOpen } from "lucide-react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";
import { useReportTileEmpty } from "./DashboardTile";

interface KbProject {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  updatedAt: string;
}

interface KnowledgeArticle {
  id: number;
  title: string;
  content: string;
  projectId: number | null;
  tags: string[] | null;
  updatedAt: string;
}

export default function KnowledgeHighlights() {
  const { data: projects, isLoading: projectsLoading } = useQuery<KbProject[]>({
    queryKey: ["/api/kb-projects"],
  });

  const { data: articles, isLoading: articlesLoading } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/knowledge"],
  });

  const isLoading = projectsLoading || articlesLoading;
  const recentArticles = articles?.slice(0, 3) || [];
  const recentProjects = projects?.slice(0, 2) || [];

  useReportTileEmpty("knowledge_highlights", !isLoading && recentArticles.length === 0 && recentProjects.length === 0);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-4 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <SectionHelp contentId="dashboard:knowledge" tooltip="Learn about the knowledge base">
            <CardTitle className="text-lg font-bold">Knowledge</CardTitle>
          </SectionHelp>
        </div>
        <Link href="/knowledge" className="flex items-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300">
          Browse <ArrowRight className="h-3 w-3" />
        </Link>
      </CardHeader>

      <CardContent className="p-4 space-y-3">
        {isLoading ? (
          <>
            <ArticleSkeleton />
            <ArticleSkeleton />
            <ArticleSkeleton />
          </>
        ) : recentProjects.length > 0 || recentArticles.length > 0 ? (
          <>
            {/* Recent KB Projects */}
            {recentProjects.map((project) => (
              <Link
                key={`proj-${project.id}`}
                href={`/knowledge/project/${project.id}`}
                className={cn(
                  "group block p-4 rounded-xl",
                  "bg-card border border-border",
                  "hover:shadow-md hover:border-emerald-100 dark:hover:border-emerald-900 transition-all duration-200"
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base"
                    style={{
                      backgroundColor: project.color ? `${project.color}20` : undefined,
                    }}
                  >
                    {project.icon || <FolderOpen className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground text-sm group-hover:text-emerald-600 transition-colors line-clamp-1">
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {project.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}

            {/* Recent articles */}
            {recentArticles.map((article) => (
              <Link
                key={`art-${article.id}`}
                href={article.projectId ? `/knowledge/project/${article.projectId}/doc/${article.id}` : `/knowledge`}
                className={cn(
                  "group block p-4 rounded-xl",
                  "bg-card border border-border",
                  "hover:shadow-md hover:border-emerald-100 dark:hover:border-emerald-900 transition-all duration-200"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground text-sm group-hover:text-emerald-600 transition-colors line-clamp-1">
                      {article.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {stripHtml(article.content).substring(0, 80)}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-end">
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Read more <ExternalLink className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
              <BookOpen className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">No knowledge projects yet</p>
            <p className="text-xs text-muted-foreground mt-1">Create a project to get started</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function stripHtml(html: string): string {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

function ArticleSkeleton() {
  return (
    <div className="block p-4 border border-border rounded-xl">
      <div className="flex items-start gap-3">
        <Skeleton className="w-8 h-8 rounded-lg" />
        <div className="flex-1">
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3 mt-1" />
        </div>
      </div>
    </div>
  );
}
